import { Clock, Calendar, PenLine, CheckCircle2, XCircle, FileCheck, MessageSquare } from "lucide-react";

// 2026-09-01 -> 01/09/2026
export const ukDate = (value) => {
  if (!value) return value;
  const m = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : value;
};

// ISO timestamp -> 01 Sept 2026, 13:23
export const ukDateTime = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"];
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())} ${months[d.getMonth()]} ${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const SIGNATURE_LABELS = {
  drawn: "Drawn signature",
  typed: "Typed signature",
  draw: "Drawn signature",
  type: "Typed signature",
};

const REVIEW_LABELS = {
  pending_review: { text: "Pending review", icon: Clock, tone: "text-[#B07A2E]" },
  accepted: { text: "Accepted", icon: CheckCircle2, tone: "text-[#205B5A]" },
  rejected: { text: "Rejected", icon: XCircle, tone: "text-red-500" },
  rejected_resubmission_requested: { text: "Rejected — resubmission requested", icon: XCircle, tone: "text-red-500" },
};

const Row = ({ icon: Icon, label, value, testid, tone = "text-[#1A1B1A]" }) => (
  <div className="flex items-start gap-2 text-xs" data-testid={testid}>
    <Icon className="w-3.5 h-3.5 text-[#757675] flex-shrink-0 mt-[1px]" />
    {label && <span className="text-[#757675]">{label}</span>}
    <span className={tone}>{value}</span>
  </div>
);

export default function AuditTrailPanel({ submission, title = "Audit Trail" }) {
  if (!submission) return null;

  const audit = submission.signature_audit || {};
  const signedAt = ukDateTime(audit.signed_at_utc || audit.signed_at || submission.signed_at_utc);
  const submittedAt = ukDateTime(submission.created_at || submission.submitted_at_utc);
  const dateSigned = ukDate(audit.date_signed_display || submission.date_signed);
  const signatureType = SIGNATURE_LABELS[submission.signature_type || audit.signature_type];
  const review = REVIEW_LABELS[submission.review_status || "pending_review"];
  // Reviewer data is only meaningful once a staff member manually accepted/rejected the form
  const hasReview = ["accepted", "rejected", "rejected_resubmission_requested"].includes(submission.review_status);

  if (!signedAt && !submittedAt && !dateSigned && !signatureType) return null;

  return (
    <div className="mt-6 pt-4 border-t border-[#1A1B1A]/[0.06]" data-testid="audit-trail-panel">
      <h4 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-3">{title}</h4>
      <div className="bg-[#FAF9F6] rounded-xl p-4 space-y-2">
        {signedAt && (
          <Row
            icon={Clock}
            label="Signed:"
            value={`${signedAt}${audit.clinic_timezone ? ` (${audit.clinic_timezone})` : ""}`}
            testid="audit-signed-at"
          />
        )}
        {dateSigned && <Row icon={Calendar} label="Date signed:" value={dateSigned} testid="audit-date-signed" />}
        {signatureType && <Row icon={PenLine} label="Signature type:" value={signatureType} testid="audit-signature-type" />}
        {submittedAt && <Row icon={FileCheck} label="Submitted:" value={submittedAt} testid="audit-submitted-at" />}
        {review && <Row icon={review.icon} label="Review status:" value={review.text} tone={review.tone} testid="audit-review-status" />}
        {hasReview && submission.reviewed_at && (
          <Row icon={Clock} label="Reviewed at:" value={ukDateTime(submission.reviewed_at)} testid="audit-reviewed-at" />
        )}
        {hasReview && submission.review_note_internal && (
          <Row icon={MessageSquare} label="Review note:" value={submission.review_note_internal} testid="audit-review-note" />
        )}
        {submission.completed_on_device && (
          <Row icon={FileCheck} label="" value="Completed on clinic device" testid="audit-on-device" tone="text-[#205B5A]" />
        )}
      </div>
    </div>
  );
}
